/**
 * 规则冲突检测（设计文档 3.5 / AC-F008-02）。
 *
 * 两规则冲突 —— 同优先级 且 条件可同时成立（区间/集合相交）。
 * 给出具体规则名与重叠字段，引导用户调整优先级化解。
 */
import type { Predicate, Rule } from './rule-model';

export interface Conflict {
  ruleA: string;
  ruleB: string;
  reason: string;
}

/** 从条件组推断 call.waitMs 的可满足区间（ms，整数）。 */
function waitMsInterval(conds: readonly Predicate[]): [number, number] {
  let lo = 0;
  let hi = Infinity;
  for (const p of conds) {
    if (p.field !== 'call.waitMs' || typeof p.value !== 'number') continue;
    const v = p.value;
    if (p.op === '>') lo = Math.max(lo, v + 1);
    else if (p.op === '>=') lo = Math.max(lo, v);
    else if (p.op === '<') hi = Math.min(hi, v - 1);
    else if (p.op === '<=') hi = Math.min(hi, v);
    else if (p.op === '==') {
      lo = Math.max(lo, v);
      hi = Math.min(hi, v);
    }
  }
  return [lo, hi];
}

function dirOf(conds: readonly Predicate[]): 1 | -1 | undefined {
  for (const p of conds) {
    if (p.field === 'call.direction' && p.op === '==') return p.value === 'up' ? 1 : -1;
  }
  return undefined;
}

function floorSet(conds: readonly Predicate[]): number[] | undefined {
  for (const p of conds) {
    if (p.field === 'call.floor' && p.op === 'in' && Array.isArray(p.value)) return p.value;
  }
  return undefined;
}

/** 两规则条件是否可能对同一呼叫同时成立。 */
function conditionsOverlap(a: Rule, b: Rule): boolean {
  const [alo, ahi] = waitMsInterval(a.conditions);
  const [blo, bhi] = waitMsInterval(b.conditions);
  if (!(alo <= bhi && blo <= ahi)) return false; // 等待区间不相交

  const da = dirOf(a.conditions);
  const db = dirOf(b.conditions);
  if (da !== undefined && db !== undefined && da !== db) return false; // 方向互斥

  const fa = floorSet(a.conditions);
  const fb = floorSet(b.conditions);
  if (fa !== undefined && fb !== undefined && !fa.some((x) => fb.includes(x))) return false; // 楼层集不相交

  return true;
}

export function findConflicts(rules: readonly Rule[]): Conflict[] {
  const out: Conflict[] = [];
  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      const a = rules[i]!;
      const b = rules[j]!;
      if (a.priority !== b.priority) continue;
      if (!conditionsOverlap(a, b)) continue;
      out.push({
        ruleA: a.name,
        ruleB: b.name,
        reason: `同优先级 ${a.priority} 且条件区间重叠 → 命中顺序不确定，请调整优先级`,
      });
    }
  }
  return out;
}
